import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styles from './AddStudentsModal.module.scss';
import Modal from '../../../components/modal/Modal';
import Button from '../../../components/button/Button';
import LiveSearch from '../../../components/liveSearch/LiveSearch';
import axiosClient from '../../../api/axiosClient';
import LoadingOverlay from '../../../components/loadingOverlay/LoadingOverlay';
import { useToasts } from '../../../contexts/ToastContext';
import ProgramStudentsTable from './ProgramStudentsTable';
import Icon from '../../../components/icon/Icon';

const AddStudentsModal = ({ isOpen, onClose, programId, onSuccess }) => {
    const { showToast } = useToasts();
    const [allStudents, setAllStudents] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedStudentIds, setSelectedStudentIds] = useState([]);

    useEffect(() => {
        if (isOpen) {
            setSelectedStudentIds([]);
            setSearchQuery('');
        }
    }, [isOpen]);

    useEffect(() => {
        const fetchAllStudents = async () => {
            if (!isOpen) return;
            
            setIsLoading(true);
            try {
                // Fetch the complete list of students globally
                const response = await axiosClient.get(`/programs/students/list`);
                let fetchedStudents = response.data?.data || response.data?.students || response.data;
                fetchedStudents = Array.isArray(fetchedStudents) ? fetchedStudents : [];
                setAllStudents(fetchedStudents);
            } catch (error) {
                console.error("Failed to fetch all students list", error);
                showToast(error.message || 'Failed to fetch students', 'error');
            } finally {
                setIsLoading(false);
            }
        };

        fetchAllStudents();
    }, [isOpen, showToast]);

    const handleSave = async () => {
        if (!programId) return;
        
        setIsSaving(true);
        try {
            await axiosClient.post(`/programs/${programId}/assign-students`, {
                studentIds: selectedStudentIds
            });
            showToast('Students assigned successfully', 'success');
            if (onSuccess) {
                onSuccess();
            }
            onClose();
        } catch (error) {
            showToast(error.message || 'Failed to assign students', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const getFilteredStudents = () => {
        if (!searchQuery) return allStudents;
        const query = searchQuery.toLowerCase();
        return allStudents.filter(s => 
            s.firstName?.toLowerCase().includes(query) || 
            s.lastName?.toLowerCase().includes(query)
        );
    };

    if (!isOpen) return null;

    return (
        <Modal scrollable={true}>
            {(isLoading || isSaving) && <LoadingOverlay />}
            <div className={styles.modalContent}>
                <div className={styles.header}>
                    <h2 className={styles.title}>Add Students</h2>
                    <div className={styles.actions}>
                        <LiveSearch 
                            placeholder="Search students" 
                            onChange={(val) => setSearchQuery(val)} 
                        />
                        <Button 
                            label="Cancel" 
                            variant="primary-light"
                            onClick={onClose}
                            size='medium'
                        />
                        <Button 
                            label={
                                <div className={styles.cta}>
                                    <Icon name="save" />
                                    Save Changes
                                </div>
                            }
                            variant="primary"
                            onClick={handleSave}
                            size='medium'
                            disabled={selectedStudentIds.length === 0}
                        />
                    </div>
                </div>

                <div className={styles.tableWrapper}>
                    <ProgramStudentsTable 
                        students={getFilteredStudents()}
                        isSelectable={true}
                        onRowSelect={(selectedIds) => setSelectedStudentIds(selectedIds)}
                        selectedRows={selectedStudentIds}
                    />
                </div>
            </div>
        </Modal>
    );
};

AddStudentsModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  programId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSuccess: PropTypes.func
};

export default AddStudentsModal;
